import { z } from 'zod';
import { PlatoError } from '../errors.ts';

export const ModelIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:/-]{0,127}$/, 'expected a provider model id');

export const ModelChoiceSchema = z.object({
  id: ModelIdSchema,
  label: z.string().trim().min(1).max(40),
});
export type ModelChoice = z.infer<typeof ModelChoiceSchema>;

export const TenantModelsSchema = z
  .object({
    choices: z.array(ModelChoiceSchema).min(1).max(8),
    default: ModelIdSchema,
    showModelIds: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    const ids = new Set<string>();
    const labels = new Set<string>();
    for (const [index, choice] of value.choices.entries()) {
      if (ids.has(choice.id)) {
        ctx.addIssue({ code: 'custom', path: ['choices', index, 'id'], message: 'duplicate id' });
      }
      const label = choice.label.toLowerCase();
      if (labels.has(label)) {
        ctx.addIssue({
          code: 'custom',
          path: ['choices', index, 'label'],
          message: 'duplicate label',
        });
      }
      ids.add(choice.id);
      labels.add(label);
    }
    if (!ids.has(value.default)) {
      ctx.addIssue({ code: 'custom', path: ['default'], message: 'not one of the choices' });
    }
  });
export type TenantModels = z.infer<typeof TenantModelsSchema>;

export function resolveModelChoice(
  models: TenantModels | undefined,
  requested: string | undefined,
): { readonly choice: ModelChoice; readonly displayName: string } | null {
  const refused = () =>
    new PlatoError('invalid_request', 'This model is not available here.', {
      details: [{ path: 'model', message: 'not one of the models this assistant offers' }],
    });
  if (models === undefined) {
    if (requested !== undefined) throw refused();
    return null;
  }
  const choice =
    requested === undefined
      ? models.choices.find((candidate) => candidate.id === models.default)
      : models.choices.find((candidate) => candidate.label === requested);
  if (choice === undefined) throw refused();
  return { choice, displayName: modelDisplayName(models, choice) };
}

export function modelDisplayName(models: TenantModels, choice: ModelChoice): string {
  return models.showModelIds ? `${choice.label} (${choice.id})` : choice.label;
}
